/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import Toast from 'react-native-toast-message';
import { formatISO, addDays } from 'date-fns';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import Colors from '@global/colors';
import { isWeb, isMobileWeb } from '@global/screens';
import { buttonRounded } from '@/assets/styles/buttonStyles';

import ModalFull from '@components/modal/ModalFull';
import SociollaText from '@components/sociolla/SociollaText';
import {
  SociollaForm,
  SociollaInput,
  SociollaSelectBox,
  SociollaDatePicker,
  SociollaSubmitButton,
  SociollaBrandSearch,
} from '@components/sociolla/form';

import {
  createLoyaltyRules,
  getLoyaltyRulesById,
  updateLoyaltyRules,
} from '@api/ms-order';
import { showPopup } from '@/store/modal';
import PropTypes from 'prop-types';

const propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool,
  isAddMode: PropTypes.bool,
  onClose: PropTypes.func,
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .test(
      'alphabets',
      'Name must contain alphabets',
      (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
    )
    .label('Name'),
  description: Yup.string(),
  is_active: Yup.mixed().required('Status is required').label('Status'),
  priority: Yup.mixed().required('Priority is required').label('Priority'),
  brands: Yup.array().required('Brand is required').label('Brand'),
  amount: Yup.number()
    .required('Amount / Value is required')
    .typeError('Amount / Value should number only')
    .min(1, 'Please add minimum 1')
    .label('Amount / Value'),
  from_date: Yup.string()
    .required('Start Date is required')
    .label('Start Date'),
  to_date: Yup.string().required('End Date is required').label('End Date'),
});

const priorityList = [
  { name: '0', value: 0 },
  { name: '1', value: 1 },
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 },
];

const statusList = [
  { name: 'Active', value: true },
  { name: 'Inactive', value: false },
];

const ModalLoyaltySetup = (props) => {
  const { id, show, isAddMode, onClose } = props;

  const dispatch = useDispatch();

  const brands = useSelector((state) => state.resources.brands);

  const [loyalty, setLoyalty] = useState(null);
  const [fromDate, setFromData] = useState('');

  const handleSubmit = async (data, { resetForm }) => {
    const payload = { ...data };
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      payload.rules = [
        {
          priority: data.priority.value,
          brands:
            data.brands && data.brands[0] && data.brands[0].id == 'all'
              ? []
              : data.brands.map((br) => {
                  return {
                    id: br.id,
                    name: br.name,
                  };
                }),
          amount: Number(data.amount),
          points: 1,
        },
      ];
      payload.from_date = formatISO(new Date(data.from_date));
      payload.to_date = formatISO(new Date(data.to_date));
      payload.is_active = data.is_active.value;
      delete payload.priority;
      delete payload.brands;
      delete payload.amount;
      if (isAddMode) {
        await createLoyaltyRules(payload);
        Toast.show({ text1: 'Rule Created', type: 'success' });
      } else {
        await updateLoyaltyRules(loyalty._id, payload);
        Toast.show({ text1: 'Rule Updated', type: 'success' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
      resetForm();
      onClose(true);
    } catch (e) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: e.response && e.response.data && e.response.data.message,
        type: 'error',
      });
    }
  };

  const convertData = (data) => {
    let priority = '';
    let brands = '';
    let amount = '';
    if (data.rules && data.rules.length) {
      const rule = data.rules[0];
      priority = priorityList.find((x) => x.value === rule.priority);
      brands = (rule.brands?.length && rule.brands) || [
        { id: 'all', name: 'All Brand' },
      ];
      amount = rule.amount;
    }
    return {
      ...data,
      is_active: statusList.find((x) => x.value === data.is_active),
      priority,
      brands,
      amount,
    };
  };

  const getById = async (id) => {
    try {
      const response = await getLoyaltyRulesById(id);
      if (response.status === 200) {
        setLoyalty(convertData(response.data.data));
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (isAddMode === false && id) {
      getById(id);
    }
  }, [isAddMode]);

  return (
    <ModalFull
      show={show}
      title={isAddMode ? 'Create Rule' : 'Edit Rule'}
      width={isWeb ? 780 : '100%'}
      onClose={() => {
        setLoyalty(null);
        onClose();
      }}>
      {(!isAddMode && !loyalty) || brands.loading ? (
        <ActivityIndicator
          size="large"
          color={Colors.blue0}
          style={{ marginBottom: 30, marginTop: isWeb ? 5 : 30 }}
        />
      ) : (
        <SociollaForm
          initialValues={{
            name: isAddMode ? '' : loyalty.name,
            description: isAddMode ? '' : loyalty.description || '',
            is_active: isAddMode ? '' : loyalty.is_active || false,
            priority: isAddMode ? '' : loyalty.priority,
            brands: isAddMode
              ? [{ id: 'all', name: 'All Brand' }]
              : loyalty.brands,
            amount: isAddMode ? '' : loyalty.amount,
            from_date: isAddMode ? '' : loyalty.from_date,
            to_date: isAddMode ? '' : loyalty.to_date,
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}>
          <ScrollView>
            <View
              style={[
                styles.container,
                styles.containerPadding,
                { paddingBottom: isWeb ? 0 : 56, flex: 1 },
              ]}>
              <View style={{ flex: 1, marginRight: isWeb ? 25 : 0 }}>
                <View style={styles.formMargin}>
                  <SociollaInput label="Rule Name" name="name" isRequired />
                </View>
                <View style={styles.formMargin}>
                  <SociollaInput
                    label="Rule Description"
                    name="description"
                    multiline={true}
                    numberOfLines={4}
                  />
                </View>
                <View style={styles.formMargin}>
                  <SociollaSelectBox
                    label="Status"
                    options={statusList}
                    name="is_active"
                    placeholder="Select Status"
                    trackBy="value"
                    labelKey="name"
                    isRequired
                  />
                </View>
              </View>
              <View style={{ flex: 1 }}>
                <View style={styles.formMargin}>
                  <SociollaSelectBox
                    label="Priority"
                    name="priority"
                    options={priorityList}
                    placeholder="Select Priority"
                    trackBy="value"
                    labelKey="name"
                    isRequired
                  />
                </View>
                <View style={styles.formMargin}>
                  <SociollaBrandSearch
                    name="brands"
                    allBrand
                    multiple
                    isRequired
                  />
                </View>
                <View
                  style={[
                    styles.formMargin,
                    { flexDirection: 'row', width: '100%' },
                  ]}>
                  <View style={{ flex: 1, marginRight: 10 }}>
                    <SociollaInput
                      keyboardType="numeric"
                      name="amount"
                      label="Value / Amount"
                      isRequired
                    />
                  </View>
                  <View
                    style={{
                      flex: 1,
                      alignSelf: 'flex-end',
                      paddingBottom: isWeb ? 10 : 20,
                    }}>
                    <SociollaText style={{ fontSize: 12 }}>
                      {' '}
                      / 1 Point
                    </SociollaText>
                  </View>
                </View>
                <SociollaText style={{ marginBottom: 5 }}>Periode</SociollaText>
                <View
                  style={[
                    styles.formMargin,
                    { flexDirection: 'row', width: '100%' },
                  ]}>
                  <View style={{ flex: 1, marginRight: isWeb ? 10 : 15 }}>
                    <SociollaDatePicker
                      label="Start Date"
                      name="from_date"
                      iconStyle={{
                        marginRight: 0,
                        marginLeft: isWeb ? -20 : isMobileWeb ? -40 : 0,
                      }}
                      isRequired
                      onChange={(val) => {
                        setFromData(formatISO(new Date(val)));
                      }}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <SociollaDatePicker
                      label="End Date"
                      name="to_date"
                      disableBeforeDate={
                        fromDate ? fromDate : addDays(new Date(), 1)
                      }
                      iconStyle={{
                        marginRight: 0,
                        marginLeft: isWeb ? -20 : isMobileWeb ? -40 : 0,
                      }}
                      isRequired
                    />
                  </View>
                </View>
              </View>
            </View>
          </ScrollView>
          <View
            style={[
              isWeb
                ? {
                    marginTop: 20,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }
                : {
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    backgroundColor: Colors.white,
                  },
              styles.containerPadding,
            ]}>
            <SociollaSubmitButton
              buttonStyle={{ ...buttonRounded, width: isWeb ? 300 : '100%' }}
              title={
                <SociollaText
                  type="bold"
                  style={{
                    fontSize: 14,
                    letterSpacing: 0.5,
                    color: Colors.white,
                    textAlign: 'center',
                  }}>
                  {isAddMode ? 'Create Rule' : 'Save Rule'}
                </SociollaText>
              }
            />
          </View>
        </SociollaForm>
      )}
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: isWeb ? 'row' : 'column',
    width: '100%',
  },
  containerPadding: {
    paddingVertical: 15,
    paddingHorizontal: 25,
  },
  formMargin: {
    marginBottom: 15,
  },
});

ModalLoyaltySetup.propTypes = propTypes;

export default ModalLoyaltySetup;
